import { useEffect, useState } from 'react';
import { CalendarClock, Loader2, Send, CheckCircle2, XCircle } from 'lucide-react';
import { baseApi, BaseApiError } from '../../../api/baseClient';
import { useAuth } from '../../../context/AuthContext';

// CORTE DIARIO AUTOMÁTICO (10-oct-2026, Bramarket: «comparar diariamente los movimientos de Lealify contra SICAR y
// Wansoft después del cierre de las 11:00 p. m.»). A la hora elegida (hora LOCAL del negocio) llega UNA vez el resumen
// por sucursal y por usuario + el Excel del día, por correo y por el WhatsApp del negocio. Solo el dueño (manda el
// Excel con los teléfonos de los clientes). Servidor: lib/dailyCut.js · /loyalty/daily-cut.
interface Cfg {
  enabled: boolean; time: string; emails: string[]; phones: string[];
  efectivos: { emails: string[]; phones: string[] }; timezone: string; sent_on: string | null;
  last?: { at: string; canales: Canal[] } | null;
}
interface Canal { canal: 'correo' | 'whatsapp'; a: string; ok: boolean; estado: string }

const HORAS = Array.from({ length: 24 * 4 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`)
  .filter((h) => h >= '18:00');
const lista = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
const tel4 = (p: string) => `…${p.slice(-4)}`;

export default function DailyCutCard() {
  const { currentUser } = useAuth();
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [emails, setEmails] = useState('');
  const [phones, setPhones] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const [prueba, setPrueba] = useState<Canal[] | null>(null);

  const esDueno = !currentUser?.baseRole || currentUser.baseRole === 'owner';

  useEffect(() => {
    if (!esDueno) return;
    let vivo = true;
    baseApi.get<Cfg>('/loyalty/daily-cut').then((c) => {
      if (!vivo) return;
      setCfg(c); setEmails((c.emails || []).join(', ')); setPhones((c.phones || []).join(', '));
    }).catch(() => {});
    return () => { vivo = false; };
  }, [esDueno]);

  if (!esDueno || !cfg) return null;

  const guardar = async (cambios: Partial<Cfg>) => {
    setSaving(true); setMsg(null);
    try {
      const r = await baseApi.put<Cfg>('/loyalty/daily-cut', cambios);
      setCfg((prev) => (prev ? { ...prev, ...r } : prev));
      setEmails((r.emails || []).join(', ')); setPhones((r.phones || []).join(', '));
      setMsg({ ok: true, t: 'Guardado' });
    } catch (e) {
      setMsg({ ok: false, t: e instanceof BaseApiError ? e.message : 'No se pudo guardar' });
    } finally { setSaving(false); }
  };

  const probar = async () => {
    setTesting(true); setPrueba(null); setMsg(null);
    try {
      const r = await baseApi.post<{ ok: boolean; canales: Canal[] }>('/loyalty/daily-cut/test', {});
      setPrueba(r.canales || []);
      if (!r.canales?.length) setMsg({ ok: false, t: 'No hay a quién enviarlo: agrega un correo o un WhatsApp.' });
    } catch (e) {
      setMsg({ ok: false, t: e instanceof BaseApiError ? e.message : 'No se pudo enviar' });
    } finally { setTesting(false); }
  };

  const input = 'w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent/50';
  const cambiaronDest = emails !== (cfg.emails || []).join(', ') || phones !== (cfg.phones || []).join(', ');

  return (
    <div className="bg-surface p-5 rounded-lg border border-line shadow-sm" data-testid="corte-diario">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <CalendarClock className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-content flex items-center gap-2">
              Corte diario automático {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            </p>
            <p className="text-xs text-content-3 mt-0.5">
              Cada día, a la hora que elijas, te llega el resumen por sucursal y por cajero y el Excel con cada movimiento
              (folio, hora, monto, cashback y quién atendió), para cuadrarlo contra tu punto de venta.
            </p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input type="checkbox" className="sr-only peer" checked={cfg.enabled} disabled={saving} data-testid="corte-encender"
            onChange={(e) => guardar({ enabled: e.target.checked })} />
          <div className="w-11 h-6 bg-surface-2 border border-line rounded-full peer-checked:bg-green-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
        </label>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-content-2">Hora (local del negocio)</label>
          <select value={cfg.time} onChange={(e) => guardar({ time: e.target.value })} disabled={saving} className={input} data-testid="corte-hora">
            {HORAS.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-content-2">Correos (separados por coma)</label>
          <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder={cfg.efectivos.emails[0] || 'tu@correo.com'} className={input} data-testid="corte-correos" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-content-2">WhatsApp (10 dígitos)</label>
          <input value={phones} onChange={(e) => setPhones(e.target.value)} placeholder={cfg.efectivos.phones[0] ? tel4(cfg.efectivos.phones[0]) : '33 1234 5678'} className={input} data-testid="corte-whatsapp" />
        </div>
      </div>
      <p className="mt-1 text-[11px] text-content-3">
        {(!cfg.emails.length || !cfg.phones.length) && 'Si lo dejas vacío llega al dueño. '}
        Llega a: {[...cfg.efectivos.emails, ...cfg.efectivos.phones.map(tel4)].join(', ') || 'nadie todavía'}.
        El WhatsApp sale por la línea de tu negocio: si no está conectada, solo llega por correo.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {cambiaronDest && (
          <button type="button" onClick={() => guardar({ emails: lista(emails), phones: lista(phones) })} disabled={saving}
            className="rounded-md bg-accent-solid px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60" data-testid="corte-guardar">
            Guardar destinatarios
          </button>
        )}
        <button type="button" onClick={probar} disabled={testing || cambiaronDest}
          className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-sm text-content-2 hover:bg-surface-2 disabled:opacity-60" data-testid="corte-probar">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar prueba con lo de hoy
        </button>
        {msg && <span className={`text-xs ${msg.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{msg.t}</span>}
      </div>

      {prueba && prueba.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs" data-testid="corte-resultado">
          {prueba.map((c, i) => (
            <li key={i} className="flex items-center gap-1.5 text-content-2">
              {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
              {c.canal === 'correo' ? 'Correo' : 'WhatsApp'} {c.a}: {c.ok ? 'enviado' : c.estado}
            </li>
          ))}
        </ul>
      )}
      {cfg.enabled && (
        <p className="mt-2 text-[11px] text-content-3">
          {cfg.sent_on ? `Último corte enviado: ${cfg.sent_on.split('-').reverse().join('/')}.` : 'Aún no se ha enviado ninguno.'} El próximo sale hoy a las {cfg.time} si aún no pasa la hora.
        </p>
      )}
    </div>
  );
}
